import { Dispatch, SetStateAction } from 'react'

import { Button, CircularProgress, InputLabel, MenuItem, Select } from '@mui/material'
import FormControl from '@mui/material/FormControl'
import { SelectChangeEvent } from '@mui/material/Select'
import { useGetAllPlaylistsQuery } from 'features/playlists/service'
import { CourseBlockState, VideoSettingsState } from 'features/videos/service'
import { Course } from 'services/courses'
import { capitalizeFirstLetter } from 'utils'

import { PlaylistSettings } from '../playlist-settings/PlaylistSettings'
import { PlaylistsSelect } from '../playlists-select/PlaylistsSelect'
import { SelectedPlaylistsTabs } from '../selected-playlists-tabs/SelectedPlaylistsTabs'

type Props = {
  block: CourseBlockState
  courses?: Course[]
  canRemove: boolean
  onRemove: () => void
  onUpdate: (updates: Partial<CourseBlockState>) => void
  setVideoSettingsState: Dispatch<SetStateAction<VideoSettingsState>>
  usedCourseIds: number[]
  videoSettingsState: VideoSettingsState
}

export const CourseBlock = ({
  block,
  courses,
  canRemove,
  onRemove,
  onUpdate,
  setVideoSettingsState,
  usedCourseIds,
  videoSettingsState,
}: Props) => {
  const { data: playlistsResponse, isLoading: isPlaylistsLoading } = useGetAllPlaylistsQuery(
    { courseId: block.courseId ? String(block.courseId) : undefined },
    { skip: !block.courseId }
  )
  const playlists = playlistsResponse?.playlists

  const availableCourses = courses?.filter(
    course => course.id === block.courseId || !usedCourseIds.includes(course.id)
  )

  const handleCourseChange = (event: SelectChangeEvent<number>) => {
    const newCourseId = event.target.value as number

    // Clean old playlist settings from this block
    setVideoSettingsState(prev => {
      const updated = { ...prev }

      block.selectedPlaylistsIds.forEach(id => delete updated[id])

      return updated
    })

    onUpdate({
      courseId: newCourseId,
      selectedPlaylistsIds: [],
      tabValue: false,
    })
  }

  const setSelectedPlaylistsIds = (ids: string[]) => {
    onUpdate({ selectedPlaylistsIds: ids })
  }

  const setTabValue = (value: boolean | string) => {
    onUpdate({ tabValue: value })
  }

  // Wrapper for PlaylistsSelect — its onSelectChange replaces the entire state,
  // which would wipe settings from other blocks. This merges block-scoped state.
  const wrappedSetVideoSettingsState: Dispatch<VideoSettingsState> = newBlockState => {
    setVideoSettingsState(prev => {
      const updated = { ...prev }

      block.selectedPlaylistsIds.forEach(id => delete updated[id])

      return { ...updated, ...newBlockState }
    })
  }

  return (
    <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <FormControl fullWidth variant={'outlined'}>
          <InputLabel>Курс *</InputLabel>
          <Select
            label={'Курс *'}
            onChange={handleCourseChange}
            required
            value={block.courseId || ''}
          >
            {availableCourses?.map(course => (
              <MenuItem key={course.id} value={course.id}>
                {capitalizeFirstLetter(course.title)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {canRemove && (
          <Button
            color={'error'}
            onClick={onRemove}
            style={{ marginLeft: 16, whiteSpace: 'nowrap' }}
          >
            Удалить курс
          </Button>
        )}
      </div>

      {block.courseId && (
        <>
          {isPlaylistsLoading ? (
            <CircularProgress size={24} />
          ) : (
            <PlaylistsSelect
              playlists={playlists}
              selectedPlaylistsIds={block.selectedPlaylistsIds}
              setSelectedPlaylistsIds={setSelectedPlaylistsIds}
              setTabValue={setTabValue}
              setVideoSettingsState={wrappedSetVideoSettingsState}
              videoSettingsState={videoSettingsState}
            />
          )}

          {block.selectedPlaylistsIds.length !== 0 && (
            <SelectedPlaylistsTabs
              playlists={playlists}
              selectedPlaylistsIds={block.selectedPlaylistsIds}
              setTabValue={setTabValue}
              tabValue={block.tabValue}
            />
          )}

          {block.selectedPlaylistsIds.map(playlistId => (
            <PlaylistSettings
              key={playlistId}
              playlistId={playlistId}
              selectedCourseId={block.courseId as number}
              selectedPlaylistsIds={block.selectedPlaylistsIds}
              setVideoSettingsState={setVideoSettingsState}
              tabValue={block.tabValue}
              videoSettingsState={videoSettingsState}
            />
          ))}
        </>
      )}
    </div>
  )
}
